import type {Component, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {doesComponentHaveLink, getAllComponentsInTree, getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';

const MAILTO_HREF = 'mailto:';
const TELEPHONE_HREF = 'tel:';
const HTTP_PROTOCOL = 'http://';
const ANCHOR_TAG_NAME = 'a';
/** Boxless wrapper: the hit region becomes the wrapped component's box, not the full row. Clients that ignore display:contents fall back to today's rendering. */
const ANCHOR_BOXLESS_STYLE = {display: 'contents'} as const;
/** The wrap-anchor styles that never paint or affect layout; anything beyond these makes a wrapper unsafe to collapse. */
const ANCHOR_PRESENTATION_ONLY_PROPERTIES = new Set<string>(['text-decoration', 'border-radius']);
export const DATA_URL_ATTRIBUTE = 'data-url';
export const HAS_CUSTOM_URL_ATTRIBUTE = 'has-custom-url';
export const EMBEDDED_VIDEO_ATTRIBUTE = 'data-is-pmw-embedded-video';

export interface ComponentAnchorOptions {
  component: Component;
  url: string;
  /** Default: true. */
  shouldOpenInNewTab?: boolean;
  /** Anchor component type to wrap with; default: PMW_LINK. */
  customLinkComponent?: string;
}

/** Wraps the component in an anchor (or sets href directly on link/button components). */
export function addComponentAnchor(editor: Editor, options: ComponentAnchorOptions): void {
  const {component, url} = options;
  const linkComponent = options.customLinkComponent ?? EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK;
  const shouldOpenInNewTab = options.shouldOpenInNewTab ?? true;

  component.addAttributes({
    [DATA_URL_ATTRIBUTE]: url,
    [HAS_CUSTOM_URL_ATTRIBUTE]: 1,
  });

  if (component.is(linkComponent) || component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON)) {
    component.addAttributes({
      href: url,
      ...(shouldOpenInNewTab ? {target: '_blank'} : {}),
    });
    return;
  }

  let anchor: Component | undefined;
  const parent = component.parent();

  if (parent?.get('type') === linkComponent) {
    anchor = parent;
    anchor.setAttributes({
      [DATA_URL_ATTRIBUTE]: url,
      href: url,
      [HAS_CUSTOM_URL_ATTRIBUTE]: 1,
    });
  } else if (parent) {
    anchor = parent.append(
      {
        type: linkComponent,
        tagName: ANCHOR_TAG_NAME,
        attributes: {
          [DATA_URL_ATTRIBUTE]: url,
          href: url,
          [HAS_CUSTOM_URL_ATTRIBUTE]: 1,
        },
      },
      {at: component.index()}
    )[0];
    component.move(anchor);
  }

  // Custom link components (social items) are real layout boxes in their row; only the default PMW_LINK wrapper is boxless.
  const isDefaultLinkWrapper = options.customLinkComponent === undefined;
  if (anchor && isDefaultLinkWrapper) {
    collapseAnchorBoxWhenSafe(anchor);
  }

  if (shouldOpenInNewTab && anchor) {
    anchor.addAttributes({
      target: '_blank',
    });
  }

  if (anchor) {
    component.set(EMAIL_MAKER_PROPS.PARENT_ANCHOR_COMPONENT_ID, anchor.getId());

    editor.Canvas.scrollTo(anchor, {
      force: true,
      behavior: 'smooth',
    });
  }

  editor.select(component);
}

/** Clears a component's link, stripping href + data-url/has-custom-url so a stale data-url can't re-surface, and unwrapping the anchor when present. */
export function removeComponentAnchor(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  component.removeAttributes(['href', DATA_URL_ATTRIBUTE, HAS_CUSTOM_URL_ATTRIBUTE]);
  component.set(EMAIL_MAKER_PROPS.PARENT_ANCHOR_COMPONENT_ID, undefined);

  const anchor = component.parent();
  if (anchor?.get('tagName')?.toLowerCase() === ANCHOR_TAG_NAME) {
    const anchorParent = anchor.parent();
    if (anchorParent !== undefined) {
      component.move(anchorParent, {at: anchor.index()});
    }
    anchor.remove();
    editor.select(component);
  }
}

/** Collapses link wrappers persisted before anchors became boxless, so old templates get the corrected hit region on load. */
export function collapseLinkWrappersOnLoad(editor: Editor): void {
  editor.getModel().skip(() => {
    editor
      .getWrapper()
      ?.findType(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK)
      .forEach((anchor) => {
        collapseAnchorBoxWhenSafe(anchor);
      });
  });
}

function collapseAnchorBoxWhenSafe(anchor: Component): void {
  if (!isCollapsibleLinkWrapper(anchor)) {
    return;
  }

  anchor.addStyle(ANCHOR_BOXLESS_STYLE);
}

function isCollapsibleLinkWrapper(anchor: Component): boolean {
  const children = anchor.components();
  if (children.length !== 1) {
    return false;
  }

  const childAttributes = children.at(0).get('attributes') as Record<string, unknown> | undefined;
  if (!childAttributes?.[DATA_URL_ATTRIBUTE]) {
    return false;
  }

  return Object.keys(anchor.getStyle()).every((property) => {
    return ANCHOR_PRESENTATION_ONLY_PROPERTIES.has(property);
  });
}

/** Keeps the recorded anchor wrapped around a dragged component. */
export function moveComponentAnchor(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  const anchorID = getStringProp(component, EMAIL_MAKER_PROPS.PARENT_ANCHOR_COMPONENT_ID);
  if (!anchorID) {
    return;
  }

  const anchor = editor.getWrapper()?.find(`#${anchorID}`)[0];
  const parent = component.parent();

  if (anchor && parent && anchor.get('tagName')?.toLowerCase() === ANCHOR_TAG_NAME) {
    anchor.move(parent, {at: component.index()});
    component.move(anchor);
  }
}

/** URL, email, or phone-number validity. */
export function isValidForLink(url: string): boolean {
  const {isValidEmail, isValidNum, isValidURL} = classifyLinkInput(url);
  return isValidEmail || isValidNum || isValidURL;
}

/** Normalizes URL/email/tel input into a linkable href value. */
export function getValidatedUrl(url: string): string {
  let value = url;
  const {isValidEmail, isValidNum, isValidURL} = classifyLinkInput(value);

  if (isValidEmail || doesInputStartWithMailto(value)) {
    value = formatEmail(value);
  } else if (isValidNum) {
    value = formatTelephoneNum(value);
  } else if (isValidURL) {
    value = formatUrl(value);
  }

  return value.trim();
}

/** Whether the anchor wraps an embedded video. */
export function isVideoLinkWrapper(component: Component | undefined): boolean {
  if (!component) {
    return false;
  }

  const attributes = component.getAttributes();
  return attributes[EMBEDDED_VIDEO_ATTRIBUTE] === 1;
}

/** Forces target=_blank on every link in the template (load listener). */
export function setTargetBlankForAllLinks(editor: Editor): void {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper) {
    return;
  }

  const components = getAllComponentsInTree(wrapper);

  for (const component of components) {
    if (doesComponentHaveLink(component) || component.get('tagName')?.toLowerCase() === ANCHOR_TAG_NAME) {
      if (component.getAttributes().target !== '_blank') {
        component.addAttributes({target: '_blank'}, {silent: true});
      }
    }
  }
}

/** Shared email/phone/URL three-way classification used by isValidForLink and getValidatedUrl. */
function classifyLinkInput(value: string): {isValidEmail: boolean; isValidNum: boolean; isValidURL: boolean} {
  const possibleEmailValue = value.toLowerCase().replace(MAILTO_HREF, '');
  const isValidURL = isUrl(value);

  return {
    isValidEmail: window.PMW.util.isValidEmail(possibleEmailValue),
    isValidNum: isPhoneNumber(value) && !isValidURL,
    isValidURL,
  };
}

function formatUrl(value: string): string {
  let newValue: string = value;
  if (!/^[a-zA-Z]+:\/\//.exec(value)) {
    newValue = HTTP_PROTOCOL + value;
  }

  return newValue;
}

function doesInputStartWithMailto(value: string): boolean {
  return value.toLowerCase().startsWith(MAILTO_HREF);
}

function formatEmail(value: string): string {
  let newValue = value.toLowerCase();

  if (!newValue.startsWith(MAILTO_HREF)) {
    newValue = MAILTO_HREF + newValue;
  }

  return newValue;
}

function formatTelephoneNum(value: string): string {
  let newValue = value.toLowerCase();

  if (!newValue.startsWith('t')) {
    newValue = TELEPHONE_HREF + newValue;
  }

  return newValue;
}

/** Whether the input looks like a web URL: scheme optional, so bare hosts, paths, query/fragment, ports, IPv4/IPv6 literals and internationalized domains all validate. */
export function isUrl(str: string): boolean {
  const lowerStr = str.toLowerCase();
  const regexp =
    /^(?:(?:https?|ftp):\/\/)?(?:[^\s:@/?#]+(?::[^\s@/?#]*)?@)?(?:localhost|\d{1,3}(?:\.\d{1,3}){3}|\[[0-9a-f:]+\]|(?:[a-z\d\u00a1-\uffff_-]+\.)+[a-z\u00a1-\uffff]{2,})(?::\d+)?(?:[/?#]\S*)?$/i;
  return regexp.test(lowerStr);
}

function isPhoneNumber(str: string): boolean {
  const sanitizedStr = str.replace(/\s/g, '');
  // No `g` flag (see isUrl).
  const regexp = /[+]?[(]?[0-9]+[)]?/i;
  return regexp.test(sanitizedStr);
}
